"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STUDIO_FOLIO_THEME = void 0;
const common_1 = require("./common");
exports.STUDIO_FOLIO_THEME = (0, common_1.firstPartyTheme)({
    id: 'studio-folio',
    name: 'Studio Folio',
    tagline: 'A portfolio that gets out of the way: gallery white, black type and one signal orange.',
    description: 'Studio Folio is a portfolio system for designers, photographers, architects, illustrators, agencies and creative studios. Gallery-white walls, black type, a single signal-orange accent, the expressive wide Syne display over neutral Inter with Geist Mono captions, sharp corners and a strict grid that lets the work be the colour. Includes a matching black-box dark mode.',
    categories: ['portfolio', 'minimal', 'editorial'],
    tokens: {
        colors: {
            background: '#fafaf8',
            surface: '#f0f0ec',
            text: '#0e0e0e',
            muted: '#6f6f6a',
            primary: '#0e0e0e',
            primaryText: '#fafaf8',
            accent: '#ff4f1f',
            border: '#e2e2dc',
        },
        darkColors: {
            background: '#0c0c0c',
            surface: '#161616',
            text: '#f2f2ee',
            muted: '#8c8c86',
            primary: '#f2f2ee',
            primaryText: '#0c0c0c',
            accent: '#ff6a3d',
            border: '#262626',
        },
        fonts: { heading: 'Syne', body: 'Inter', mono: 'Geist Mono' },
        radius: 0,
        density: 'comfortable',
        typeScale: 1.414,
    },
    guide: `## Mood
A white-walled gallery with one bold sign on the door. The work is the colour; the site is the frame. Confident, curated, slightly irreverent in the type, ruthless in the edit. A visitor should understand who you are in one line and see your best project in one scroll.

## Grid and layout
- A strict 12-column grid with narrow gutters (16–24px) and edge-to-edge images. Expose the grid in small ways: index numbers, column-aligned captions, thin rules.
- The home page is an index of work, not a sales page. Lead with a one-line statement, then projects immediately.
- Project grid: mix spans (8+4, 4+4+4, 12) so the page has rhythm; keep each project's cover in its native aspect ratio, never cropped to identical squares.
- Case studies read top to bottom: title block, a full-bleed hero image, a short facts row (client, year, role, discipline), then alternating text and image sequences.

## Typography
- Syne for display, weight 700–800, very large: the hero statement at the 5xl step can run edge to edge. Use uppercase Syne for project titles on covers.
- Inter for body at 16–17px, weight 400, line-height 1.6.
- Geist Mono for captions, index numbers ("(04)"), years, tags and the facts row, 12–13px, muted.
- Never more than three type sizes per view outside the hero.

## Colour
Gallery white, black ink, grey captions. Signal orange appears once or twice per page: the hover state on project titles, the availability dot ("● Available for projects from March"), a cursor follower, the active filter. In dark mode the frame turns black and the orange stays.

## Imagery
The work itself, at the highest quality, uncropped, on the background colour or full bleed. Process shots (sketches, studio tables, mood boards) are welcome in case studies. Video loops for motion work, muted and autoplaying inline. No stock photography, no device mockups unless the project is a digital product.

## Motion
Editorial and precise: images reveal with a clip-path wipe (600ms, ease-in-out), project titles slide on hover, and a small cursor label ("View") follows the pointer over project covers on desktop. Page transitions are a fast fade. Avoid bouncy springs and parallax overload.

## Copy tone
Brief, first person or studio "we", specific about outcomes and roles. "Brand identity and packaging for a natural wine bar in Lisbon. 2025." Avoid adjectives like passionate, creative, innovative. Let clients and numbers speak in case studies.

## Do
- Show the best 6–9 projects only; quality over quantity.
- Put contact (email as a big mailto link) and availability on every page footer.
- Include filters by discipline if there are more than nine projects.

## Don't
- No rounded corners, gradients or card shadows.
- No skill bars, percentage meters or logo walls with fifty clients.
- Don't let UI chrome compete with the work.`,
    sections: [
        {
            name: 'Hero: statement',
            recipe: 'Top of the home page: a Syne statement at the 5xl step spanning 10 columns ("Independent design studio for brands that make things."), a Geist Mono line below with location, local time and an orange availability dot. No hero image; the project index starts within the first viewport.',
        },
        {
            name: 'Project index',
            recipe: 'A grid of project covers with mixed spans on the 12-column grid. Each cover: the image in its native ratio, then a caption row aligned to columns: index number "(01)" in mono, project title in uppercase Syne (lg), discipline and year in mono muted, right-aligned. On hover the title turns orange and a "View" cursor label follows the pointer.',
        },
        {
            name: 'Case study facts',
            recipe: 'Below the case study hero image: a 4-column row of facts (Client, Year, Role, Discipline) with mono labels in muted 12px and values in Inter 16px, separated from the content by a 1px rule. Then a 7-column intro paragraph starting at column 4.',
        },
        {
            name: 'Contact footer',
            recipe: 'A large footer on the background colour: "Let\'s talk" small mono label, the email address as a huge Syne mailto link (4xl) that underlines in orange on hover, then a row of social links in mono and the availability dot. Dark mode makes the footer black.',
        },
    ],
});
